import React from 'react';
import i18n from 'util/i18n';
import ValidationElement from 'components/Form/ValidationElement';
import { ValidationElementProps } from 'components/Form/ValidationElement/ValidationElement';
import {
  CommonOnUpdate, CommonOnError, CommonScrollIntoView, CommonRequired
} from 'types/props';
import { ErrorCode } from 'redux/modules/application/reducers/error';
import {
  LegalInvestigationsHistoryItemModel,
  LegalInvestigationsInvestigatingAgencyModel,
} from 'forms/sections/legal/investigation/history/types';
import Field from 'components/Form/Field';
import NotApplicable from 'components/Form/NotApplicable';
import DateControl from 'components/Form/DateControl';
import Branch from 'components/Form/Branch';
import Text from 'components/Form/Text';
import { isPVQForm } from 'helpers/form';
import Textarea from 'components/Form/Textarea';
import ClearanceLevel from './ClearanceLevel';
import InvestigatingAgency from './InvestigatingAgency';

interface Props extends Partial<LegalInvestigationsHistoryItemModel>, ValidationElementProps {
  onUpdate?: CommonOnUpdate
  onError?: CommonOnError
  required?: CommonRequired
  scrollIntoView?: CommonScrollIntoView
  requireLegalInvestigationClearanceGranted?: boolean
  formType?: string
}

export default class HistoryItem extends ValidationElement<Props> {
  update = (queue: Partial<LegalInvestigationsHistoryItemModel>) => {
    this.props.onUpdate({
      AgencyNotApplicable: this.props.AgencyNotApplicable,
      Agency: this.props.Agency,
      AgencyExplanation: this.props.AgencyExplanation,
      CompletedNotApplicable: this.props.CompletedNotApplicable,
      Completed: this.props.Completed,
      CompletedComments: this.props.CompletedComments,
      Issued: this.props.Issued,
      GrantedNotApplicable: this.props.GrantedNotApplicable,
      Granted: this.props.Granted,
      GrantedComments: this.props.GrantedComments,
      ClearanceLevelNotApplicable: this.props.ClearanceLevelNotApplicable,
      ClearanceLevel: this.props.ClearanceLevel,
      ClearanceGranted: this.props.ClearanceGranted,
      ...queue,
    });
  };

  updateAgencyNotApplicable = (
    values: LegalInvestigationsHistoryItemModel['AgencyNotApplicable']
  ) => {
    this.update({
      AgencyNotApplicable: values,
      Agency: {} as LegalInvestigationsHistoryItemModel['Agency'],
      AgencyExplanation: {} as LegalInvestigationsHistoryItemModel['AgencyExplanation'],
    });
  };

  updateAgency = (values: LegalInvestigationsInvestigatingAgencyModel) => {
    this.update({
      Agency: values.Agency,
      AgencyExplanation: values.Explanation,
    });
  };

  updateAgencyPvq = (values: Partial<LegalInvestigationsHistoryItemModel>) => {
    this.update({
      Agency: values.Agency,
      AgencyExplanation: values.Agency?.value === 'Other' ? this.props.AgencyExplanation : {} as LegalInvestigationsHistoryItemModel['AgencyExplanation'],
    });
  };

  updateAgencyExplanation = (value: LegalInvestigationsHistoryItemModel['AgencyExplanation']) => {
    this.update({
      AgencyExplanation: value
    });
  };

  updateCompletedNotApplicable = (
    values: LegalInvestigationsHistoryItemModel['CompletedNotApplicable']
  ) => {
    this.update({
      CompletedNotApplicable: values,
      Completed: {
        month: '',
        day: '',
        year: '',
        estimate: false,
      },
    });
  };

  updateCompleted = (values: LegalInvestigationsHistoryItemModel['Completed']) => {
    this.update({
      Completed: values,
    });
  };

  updateIssued = (values: LegalInvestigationsHistoryItemModel['Issued']) => {
    this.update({
      Issued: values,
    });
  };

  updateGrantedNotApplicable = (
    values: LegalInvestigationsHistoryItemModel['GrantedNotApplicable']
  ) => {
    this.update({
      GrantedNotApplicable: values,
      Granted: {},
    });
  };

  updateGranted = (values: LegalInvestigationsHistoryItemModel['Granted']) => {
    this.update({
      Granted: values,
    });
  };

  updateClearanceLevelNotApplicable = (
    values: LegalInvestigationsHistoryItemModel['ClearanceLevelNotApplicable']
  ) => {
    this.update({
      ClearanceLevelNotApplicable: values,
      ClearanceLevel: {},
    });
  };

  updateClearanceLevel = (values: LegalInvestigationsHistoryItemModel['ClearanceLevel']) => {
    this.update({
      ClearanceLevel: values,
    });
  };

  updateClearanceGranted = (values: LegalInvestigationsHistoryItemModel['ClearanceGranted']) => {
    this.update({
      ClearanceGranted: values,
      Issued: {},
      Granted: {},
      ClearanceLevel: {},
    });
  };

  render() {
    const { requireLegalInvestigationClearanceGranted } = this.props;

    const attribute = `List.items[${this.props.$index}].Item`;
    const isPVQ = isPVQForm(this.props.formType);

    return isPVQ ? (
      <div>
        <Field
          title={i18n.t('legal.pvq.investigations.history.heading.agency')}
          adjustFor="big-buttons"
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.Agency`}
          exactAttribute
        >
          <InvestigatingAgency
            {...{
              Agency: this.props.Agency,
              Explanation: this.props.AgencyExplanation,
            }}
            name="Agency"
            onUpdate={this.updateAgencyPvq}
            onError={this.props.onError}
            className="legal-investigations-history-agency"
            required={this.props.required && this.props.AgencyNotApplicable.applicable}
            scrollIntoView={this.props.scrollIntoView}
            attribute={attribute}
            formType={this.props.formType}
          />
        </Field>

        {this.props.Agency?.value === 'Other'
          && (
          <Field
            title={i18n.t('legal.pvq.investigations.history.heading.agencyexplanation')}
            scrollIntoView={this.props.scrollIntoView}
            attribute={`${attribute}.AgencyExplanation`}
            exactAttribute
          >
            <Textarea
              {...this.props.AgencyExplanation}
              name="AgencyExplanation"
              className="legal-investigations-history-agency-explanation"
              onUpdate={this.updateAgencyExplanation}
              onError={this.props.onError}
              required={this.props.required}
              disabled={this.props.disabled}
              attribute={`${this.props.attribute}.AgencyExplanation`}
            />
          </Field>
          )}

      </div>
    ) : (
      <div>
        <Field
          title={i18n.t('legal.investigations.history.heading.agency')}
          adjustFor="big-buttons"
          scrollIntoView={this.props.scrollIntoView}
        >
          <NotApplicable
            name="AgencyNotApplicable"
            className="legal-investigations-history-agency-notapplicable"
            {...this.props.AgencyNotApplicable}
            onUpdate={this.updateAgencyNotApplicable}
            onError={this.props.onError}
            or={i18n.m('legal.investigations.history.para.or')}
            label={i18n.t('legal.investigations.history.label.idk')}
            required={this.props.required}
            attribute={`${attribute}.AgencyNotApplicable`}
            includeNAConfirmation={Boolean(this.props.Agency?.value || this.props.AgencyExplanation?.value)}
          >
            <InvestigatingAgency
              name="Agency"
              {...{
                Agency: this.props.Agency,
                Explanation: this.props.AgencyExplanation,
              }}
              onUpdate={this.updateAgency}
              onError={this.props.onError}
              className="legal-investigations-history-agency"
              required={this.props.required && this.props.AgencyNotApplicable.applicable}
              scrollIntoView={this.props.scrollIntoView}
              attribute={attribute}
            />
          </NotApplicable>
        </Field>

        <Field
          title={i18n.t('legal.investigations.history.heading.completed')}
          help="legal.investigations.history.help.completed"
          adjustFor="datecontrol"
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.Completed`}
        >
          <NotApplicable
            name="CompletedNotApplicable"
            className="legal-investigations-history-completed-notapplicable"
            {...this.props.CompletedNotApplicable}
            onUpdate={this.updateCompletedNotApplicable}
            onError={this.props.onError}
            or={i18n.m('legal.investigations.history.para.or')}
            label={i18n.t('legal.investigations.history.label.idk')}
            required={this.props.required}
            attribute={`${attribute}.CompletedNotApplicable`}
            includeNAConfirmation={Boolean(this.props.Completed?.month || this.props.Completed?.year)}
          >
            <DateControl
              name="Completed"
              {...this.props.Completed}
              onUpdate={this.updateCompleted}
              onError={this.props.onError}
              minDateEqualTo
              className="legal-investigations-history-completed"
              required={this.props.required}
              hideDay
              attribute={`${attribute}.Completed`}
            />
          </NotApplicable>
        </Field>

        {requireLegalInvestigationClearanceGranted && (
          <Branch
            label={i18n.t('legal.investigations.history.heading.wasClearanceGranted')}
            labelSize="h4"
            warning
            onError={this.props.onError}
            required={this.props.required}
            onUpdate={this.updateClearanceGranted}
            scrollIntoView={this.props.scrollIntoView}
            {...this.props.ClearanceGranted}
            attribute={`${attribute}.ClearanceGranted`}
          />
        )}

        {((requireLegalInvestigationClearanceGranted
          && this.props.ClearanceGranted
          && this.props.ClearanceGranted.value === 'Yes')
          || !requireLegalInvestigationClearanceGranted) && (
            <span>
              <Field
                title={i18n.t('legal.investigations.history.heading.issued')}
                adjustFor="text"
                optional
                scrollIntoView={this.props.scrollIntoView}
                attribute={`${attribute}.Issued`}
              >
                <Text
                  name="Issued"
                  {...this.props.Issued}
                  onUpdate={this.updateIssued}
                  onError={this.props.onError}
                  className="legal-investigations-history-issued"
                  attribute={`${attribute}.Issued`}
                />
              </Field>

              <Field
                title={i18n.t('legal.investigations.history.heading.granted')}
                help="legal.investigations.history.help.granted"
                adjustFor="datecontrol"
                scrollIntoView={this.props.scrollIntoView}
                attribute={`${attribute}.Granted`}
              >
                <NotApplicable
                  name="GrantedNotApplicable"
                  className="legal-investigations-history-granted-notapplicable"
                  {...this.props.GrantedNotApplicable}
                  onUpdate={this.updateGrantedNotApplicable}
                  onError={this.props.onError}
                  or={i18n.m('legal.investigations.history.para.or')}
                  label={i18n.t('legal.investigations.history.label.idk')}
                  required={this.props.required}
                  attribute={`${attribute}.GrantedNotApplicable`}
                  includeNAConfirmation={Boolean(this.props.Granted?.month || this.props.Granted?.year)}
                >
                  <DateControl
                    name="Granted"
                    prefix={
                      this.props.CompletedNotApplicable?.applicable ? 'clearanceGranted' : undefined
                    }
                    {...this.props.Granted}
                    onUpdate={this.updateGranted}
                    minDate={this.props.Completed}
                    minDateEqualTo
                    onError={this.props.onError}
                    className="legal-investigations-history-granted"
                    required={this.props.required}
                    hideDay
                    attribute={`${attribute}.Granted`}
                  />
                </NotApplicable>
              </Field>

              <Field
                title={i18n.t('legal.investigations.history.heading.clearance')}
                adjustFor="big-button"
                scrollIntoView={this.props.scrollIntoView}
                attribute={`${attribute}.ClearanceLevel`}
              >
                <NotApplicable
                  name="ClearanceLevelNotApplicable"
                  className="legal-investigations-history-clearance-notapplicable"
                  {...this.props.ClearanceLevelNotApplicable}
                  onUpdate={this.updateClearanceLevelNotApplicable}
                  onError={this.props.onError}
                  or={i18n.m('legal.investigations.history.para.or')}
                  label={i18n.t('legal.investigations.history.label.idk')}
                  required={this.props.required}
                  attribute={`${attribute}.ClearanceLevelNotApplicable`}
                  includeNAConfirmation={Boolean(this.props.ClearanceLevel?.Level?.value || this.props.ClearanceLevel?.Explanation?.value)}
                >
                  <ClearanceLevel
                    name="ClearanceLevel"
                    {...this.props.ClearanceLevel}
                    onUpdate={this.updateClearanceLevel}
                    onError={this.props.onError}
                    className="legal-investigations-history-clearance"
                    required={this.props.required}
                    scrollIntoView={this.props.scrollIntoView}
                    attribute={`${attribute}.ClearanceLevel`}
                  />
                </NotApplicable>
              </Field>
            </span>
        )}
      </div>
    );
  }
}

HistoryItem.defaultProps = {
  required: false,
  onUpdate: () => { },
  onError: (value, arr: ErrorCode[]) => arr,
  AgencyNotApplicable: { applicable: true },
  CompletedNotApplicable: { applicable: true },
  GrantedNotApplicable: { applicable: true },
  ClearanceLevelNotApplicable: { applicable: true },
  requireLegalInvestigationClearanceGranted: false,
  AgencyExplanation: {},
};
